import { ClientAppId, Dossier } from '../models';
import { getOperationType } from './claim-utils';

export interface CoverValueAbsolute {
  val: number;
}

export interface CoverValuePercentage {
  rate: number;
}

export interface CoverValueCustom {
  custom: string;
}

export interface CoverValueRange {
  range:
    | {
        min: number;
        max?: number;
      }
    | {
        min?: number;
        max: number;
      };
}

export type CoverValue = ({} | CoverValueRange) &
  (CoverValueAbsolute | CoverValuePercentage | CoverValueCustom);

export const CoverBDGValues = {
  COVER_BDG_TAG_NONE: '',
  COVER_BDG_TAG_BASE: 'H',
  COVER_BDG_TAG_ONLY_WS: 'HPB',
  COVER_BDG_TAG_NO_EXCESS: 'HSF',
  COVER_BDG_TAG_ONLY_WS_NO_EXCESS: 'HPBSF',
} as const;
export type CoverBDGValues = typeof CoverBDGValues[keyof typeof CoverBDGValues];

export interface IsCoveredOptions {
  coverAllGlasses: boolean;
  defaultIsInsuredWithoutGlass?: boolean;
}

export interface MessageInfos {
  key: string;
  params?: any;
}

export function isTagBDGNotCovered(dossier: Dossier) {
  return dossier?.policyInformation?.coverBDG === '';
}

export function isTagBDGCoverUnknown(dossier: Dossier) {
  return (
    dossier?.policyInformation?.coverBDG === null ||
    dossier?.policyInformation?.coverBDG === undefined
  );
}

export function isTagBDGCovered(dossier: Dossier) {
  return !isTagBDGNotCovered(dossier) && !isTagBDGCoverUnknown(dossier);
}

export function isTagBDGCoverOnlyWindshield(dossier: Dossier) {
  switch (dossier?.policyInformation?.coverBDG) {
    case CoverBDGValues.COVER_BDG_TAG_ONLY_WS:
    case CoverBDGValues.COVER_BDG_TAG_ONLY_WS_NO_EXCESS:
      return true;
    default:
      return false;
  }
}

export function isTagBDGNoExcess(dossier: Dossier) {
  switch (dossier?.policyInformation?.coverBDG) {
    case CoverBDGValues.COVER_BDG_TAG_NO_EXCESS:
    case CoverBDGValues.COVER_BDG_TAG_ONLY_WS_NO_EXCESS:
      return true;
    default:
      return false;
  }
}

export function hasAnyCoverValue(dossier: Dossier) {
  return (
    typeof dossier?.policyInformation?.repairDeductible === 'number' ||
    typeof dossier?.policyInformation?.replacementDeductible === 'number' ||
    typeof dossier?.policyInformation?.maxReplacementDeductible === 'number' ||
    typeof dossier?.policyInformation?.minReplacementDeductible === 'number' ||
    typeof dossier?.policyInformation?.maxRepairDeductible === 'number' ||
    typeof dossier?.policyInformation?.minRepairDeductible === 'number' ||
    typeof dossier?.policyInformation?.repairRateDeductible === 'number' ||
    typeof dossier?.policyInformation?.replacementRateDeductible === 'number'
  );
}

export function isTaggedAsCovered(dossier: Dossier) {
  return !!dossier?.policyInformation?.isCovered || isTagBDGCovered(dossier);
}

export function isInconsistentCover(dossier: Dossier) {
  const coverBDGNotCovered =
    isTagBDGCovered(dossier) && dossier?.policyInformation?.isCovered === false;

  return (
    (isTaggedAsCovered(dossier) &&
      !isTagBDGNoExcess(dossier) &&
      !hasAnyCoverValue(dossier)) ||
    coverBDGNotCovered
  );
}

export function isInsured(dossier: Dossier):
  | true
  | {
      notInsuredReason:
        | 'api'
        | 'cancelled'
        | 'suspended'
        | 'notCovered'
        | 'wrongDates'
        | 'unknown';
    } {
  if (
    !dossier ||
    !dossier?.policyInformation ||
    (typeof dossier.policyInformation
      .isGuaranteeConditionRetrieveWithSuccess !== 'boolean' &&
      typeof dossier.policyInformation.isCovered !== 'boolean')
  ) {
    return { notInsuredReason: 'unknown' };
  }

  if (
    dossier.policyInformation.isGuaranteeConditionRetrieveWithSuccess ===
      false ||
    isInconsistentCover(dossier)
  ) {
    return { notInsuredReason: 'api' };
  }

  if (
    dossier.policyInformation.isCancelled ||
    dossier.policyInformation.isCancelledWithoutPayment
  ) {
    return { notInsuredReason: 'cancelled' };
  }

  if (dossier.policyInformation.isSuspended) {
    return { notInsuredReason: 'suspended' };
  }

  if (!dossier.policyInformation.isCovered || isTagBDGNotCovered(dossier)) {
    return { notInsuredReason: 'notCovered' };
  }

  if (dossier?.claim?.sinisterDate) {
    const sinisterDate = new Date(dossier.claim.sinisterDate);
    if (
      (!!dossier?.policyInformation?.insuranceStartDate &&
        sinisterDate <
          new Date(dossier.policyInformation.insuranceStartDate)) ||
      (!!dossier?.policyInformation?.insuranceEndDate &&
        sinisterDate > new Date(dossier.policyInformation.insuranceEndDate))
    ) {
      return { notInsuredReason: 'wrongDates' };
    }
  }

  return true;
}

export function isCovered(
  dossier: Dossier,
  options: IsCoveredOptions
):
  | true
  | {
      notCoveredReason: 'notInsured' | 'wrongGlass' | 'unknown';
    } {
  if (!dossier || !dossier?.claim || !dossier?.policyInformation) {
    return {
      notCoveredReason: 'unknown',
    };
  }

  if (isInsured(dossier) !== true) {
    return {
      notCoveredReason: 'notInsured',
    };
  }

  if (!dossier.claim.glass?.glass) {
    return (
      (!!options.defaultIsInsuredWithoutGlass &&
        !!dossier.policyInformation.isCovered) || {
        notCoveredReason: 'unknown',
      }
    );
  }

  const isWindshield = dossier.claim.glass?.glass === 'windshield';
  const isCoverableGlass =
    dossier.claim.glass?.glass === 'rearWindow' ||
    dossier.claim.glass?.glass === 'sideWindow' ||
    options.coverAllGlasses;

  if (!isWindshield && !isCoverableGlass) {
    return {
      notCoveredReason: 'wrongGlass',
    };
  }

  if (isTagBDGCoverOnlyWindshield(dossier)) {
    return (
      isWindshield || {
        notCoveredReason: 'wrongGlass',
      }
    );
  }

  if (isTagBDGCovered(dossier)) {
    return true;
  }

  return (
    !!dossier.policyInformation.isCovered || {
      notCoveredReason: 'unknown',
    }
  );
}

export function canBookWithInsurance(
  dossier: Dossier,
  options: IsCoveredOptions,
  disableBookWithoutInsurance: boolean
) {
  return isCovered(dossier, options) === true || !disableBookWithoutInsurance;
}

export function isFullyCovered(dossier: Dossier, options: IsCoveredOptions) {
  const covered = isCovered(dossier, options);

  if (covered === true) {
    const isRepair = dossier?.claim?.operationType
      ? dossier?.claim?.operationType === 'Repair'
      : getOperationType(dossier?.claim?.glass);

    if ((isRepair && isTagBDGCovered(dossier)) || isTagBDGNoExcess(dossier)) {
      return true;
    }
  }

  return false;
}

export function getCoverValue(
  dossier: Dossier,
  options: IsCoveredOptions
): CoverValue {
  if (isCovered(dossier, options) !== true) {
    return {
      rate: 0,
    };
  }

  if (isFullyCovered(dossier, options)) {
    return {
      rate: 100,
    };
  }

  const isRepair = dossier?.claim?.operationType
    ? dossier?.claim?.operationType === 'Repair'
    : getOperationType(dossier?.claim?.glass);

  let resultCover: CoverValue = {
    rate: 15,
  };

  if (isRepair) {
    if (dossier.policyInformation.repairDeductible) {
      resultCover = {
        val: dossier.policyInformation.repairDeductible,
      };
    } else if (dossier.policyInformation.repairRateDeductible) {
      resultCover = {
        rate: dossier.policyInformation.repairRateDeductible,
      };
    }

    if (
      dossier.policyInformation.maxRepairDeductible ||
      dossier.policyInformation.maxRepairDeductible === 0 ||
      dossier.policyInformation.minRepairDeductible ||
      dossier.policyInformation.minRepairDeductible === 0 ||
      dossier.policyInformation.repairGuaranteeCeiling
    ) {
      resultCover = {
        ...resultCover,
        range: {
          min: dossier.policyInformation.minRepairDeductible,
          max:
            dossier.policyInformation.maxRepairDeductible ||
            dossier.policyInformation.repairGuaranteeCeiling,
        },
      };
    }
  } else {
    if (
      dossier.policyInformation.replacementDeductible ||
      dossier.policyInformation.replacementDeductible === 0
    ) {
      resultCover = {
        val: dossier.policyInformation.replacementDeductible,
      };
    } else if (dossier.policyInformation.replacementRateDeductible) {
      resultCover = {
        rate: dossier.policyInformation.replacementRateDeductible,
      };
    }

    if (
      dossier.policyInformation.maxReplacementDeductible ||
      dossier.policyInformation.maxReplacementDeductible === 0 ||
      dossier.policyInformation.minReplacementDeductible ||
      dossier.policyInformation.minReplacementDeductible === 0 ||
      dossier.policyInformation.replacementGuaranteeCeiling
    ) {
      resultCover = {
        ...resultCover,
        range: {
          min: dossier.policyInformation.minReplacementDeductible,
          max:
            dossier.policyInformation.maxReplacementDeductible ||
            dossier.policyInformation.replacementGuaranteeCeiling,
        },
      };
    }
  }

  return resultCover;
}

export function getInsuredMessage(
  dossier: Dossier,
  options: IsCoveredOptions,
  clientAppId: ClientAppId
) {
  const insuredResult = isInsured(dossier);
  const coverResult = isCovered(dossier, options);
  const onlyWindshield = isTagBDGCoverOnlyWindshield(dossier);

  let resTrad: MessageInfos;

  if (insuredResult === true) {
    if (coverResult === true) {
      if (onlyWindshield) {
        resTrad = {
          key: 'coveredOnlyWindshield',
        };
      } else {
        resTrad = {
          key: 'coveredAnyGlass',
        };
      }
    } else {
      switch (coverResult.notCoveredReason) {
        case 'wrongGlass':
          resTrad = {
            key: 'notCoveredGlass',
          };
          break;
        case 'notInsured': // <--- This one should not happen
        case 'unknown':
        default:
          resTrad = {
            key: 'noneCovered',
          };
          break;
      }
    }
  } else {
    switch (insuredResult.notInsuredReason) {
      case 'api':
        resTrad = {
          key: 'notCoveredApiError',
        };
        break;
      case 'cancelled':
        resTrad = {
          key: 'notCoveredCancelled',
        };
        break;
      case 'suspended':
        resTrad = {
          key: 'notCoveredSuspended',
        };
        break;
      case 'notCovered':
        resTrad = {
          key: 'noneCovered',
        };
        break;
      case 'wrongDates':
        resTrad = {
          key: 'notCoveredDates',
        };
        break;
      case 'unknown':
      default:
        resTrad = {
          key: 'notCoveredUnknown',
        };
        break;
    }
  }

  resTrad.key = `GlobalApplicativeConstants.coverAlternatives.coverCauses.${resTrad.key}.${clientAppId}`;

  return resTrad;
}

export function getCoverMessage(
  dossier: Dossier,
  options: IsCoveredOptions,
  clientAppId: ClientAppId | 'email'
) {
  const insuredResult = isInsured(dossier);

  let resultTranslate: MessageInfos;

  const isApiError =
    insuredResult !== true &&
    (insuredResult.notInsuredReason === 'api' ||
      insuredResult.notInsuredReason === 'unknown');

  if (isApiError) {
    resultTranslate = {
      key: 'coverUnknown',
    };
  } else {
    const coverValue = getCoverValue(dossier, options);

    const isRepair = dossier?.claim?.operationType
      ? dossier?.claim?.operationType === 'Repair'
      : getOperationType(dossier?.claim?.glass);

    if ('rate' in coverValue) {
      if (coverValue.rate == 0) {
        resultTranslate = {
          key: 'coverNone',
        };
      } else if (coverValue.rate == 100) {
        if (isRepair) {
          resultTranslate = {
            key: 'coverFullRepair',
          };
        } else {
          resultTranslate = {
            key: 'coverFull',
          };
        }
      } else {
        resultTranslate = {
          key: 'coverRate',
          params: {
            ...coverValue,
          },
        };
      }
    } else if ('val' in coverValue) {
      if (coverValue.val === 0) {
        resultTranslate = {
          key: 'coverFull',
        };
      } else
        resultTranslate = {
          key: 'coverVal',
          params: {
            ...coverValue,
          },
        };
    } else if ('custom' in coverValue && !!coverValue.custom) {
      resultTranslate = {
        key: 'coverCustom',
        params: {
          ...coverValue,
        },
      };
    } else {
      resultTranslate = {
        key: 'coverRate',
        params: {
          rate: 15,
        },
      };
    }
  }

  resultTranslate.key = `GlobalApplicativeConstants.coverAlternatives.coverValues.${resultTranslate.key}.${clientAppId}`;

  return resultTranslate;
}

export function getCoverRangeMessage(
  dossier: Dossier,
  options: IsCoveredOptions,
  clientAppId: ClientAppId | 'email'
) {
  const coverValue = getCoverValue(dossier, options);

  let resultTranslate: MessageInfos = null;

  if ('range' in coverValue) {
    if (!!coverValue.range.min && !coverValue.range.max) {
      resultTranslate = {
        key: 'coverMin',
        params: {
          ...coverValue.range,
        },
      };
    } else if (!!coverValue.range.max && !coverValue.range.min) {
      resultTranslate = {
        key: 'coverMax',
        params: {
          ...coverValue.range,
        },
      };
    } else if (!!coverValue.range.min && !!coverValue.range.max) {
      resultTranslate = {
        key: 'coverMinMax',
        params: {
          ...coverValue.range,
        },
      };
    }
  }

  if (!!resultTranslate) {
    resultTranslate.key = `GlobalApplicativeConstants.coverAlternatives.coverRange.${resultTranslate.key}.${clientAppId}`;
  }

  return resultTranslate;
}
